import React, { useEffect, useState, useMemo } from 'react';
import { Card, Typography, Tag, Space, Button, Row, Col, Avatar, Spin,
  Divider, Empty, Progress, Form, Input, DatePicker, Select,
  Switch, Collapse, Tooltip, Upload, message
} from 'antd';
import {
  ArrowLeftOutlined, BankOutlined, MailOutlined, PhoneOutlined,
  GlobalOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined,
  InfoCircleOutlined, EditOutlined, SaveOutlined, CloseOutlined,
  LockOutlined, UnlockOutlined, SettingOutlined, PlusOutlined, DeleteOutlined,
  FileTextOutlined, CameraOutlined, EnvironmentOutlined, AimOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { tenantActions } from '../../actions/tenantActions';
import { subscriptionActions } from '../../actions/subscriptionActions';
import { userActions } from '../../actions/userActions';
import { commonActions } from '../../actions/commonActions';
import dayjs from 'dayjs';
import { TbTrophy } from 'react-icons/tb';
import SpinnerLoader from '../../components/spinnerLoader';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const BASE_LABELS: Record<string, string> = {
  isAllowed: 'Page Access',
  isAdd: 'Add',
  isEdit: 'Edit',
  isView: 'View',
};

const BATCH_ONLY_LABELS: Record<string, string> = {
  isCalendarView: 'Calendar View',
  isLocationTracking: 'Location Tracking',
  isLocationSharing: 'Location Sharing',
};

const BATCH_ROUTE = '/batches';
const PROFILE_SETTINGS_ROUTES = ['/profile', '/settings'];
const DASHBOARD_ONLY_ROUTES = ['/dashboard'];

const getFieldsForRoute = (route: string, isFromSubscription?: boolean) => {
  if (DASHBOARD_ONLY_ROUTES.includes(route)) return { isAllowed: 'Page Access' };
  if (route === '/settings') return { isAllowed: 'Page Access', isEdit: 'Edit', isChatEnabled: 'Chat Assistant' };
  if (route === '/profile') return { isAllowed: 'Page Access', isEdit: 'Edit' };
  if (route === BATCH_ROUTE) return { ...BASE_LABELS, ...BATCH_ONLY_LABELS };
  return BASE_LABELS;
};

const DEFAULT_ACCESS_POINT = {
  isAllowed: true, isAdd: true, isEdit: true, isView: true,
  isCalendarView: false, isLocationTracking: false, isLocationSharing: false,
};

interface Role { role?: string; }

const TenantView: React.FC<Role> = ({ role }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const tenantIdFromState = location.state?.tenantId;
  const [form] = Form.useForm();
  const [isEditMode, setIsEditMode] = useState(false);
  const [accessPoints, setAccessPoints] = useState<any[]>([]);
  const [accessEditMode, setAccessEditMode] = useState(false);
  const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);

  // Profile image states
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [imageDeleted, setImageDeleted] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadedImageId, setUploadedImageId] = useState<string | null>(null);

  // Location state
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [coordinates, setCoordinates] = useState<{ latitude: string; longitude: string }>({ latitude: '', longitude: '' });

  const tenantViewState = useSelector((state: any) => state.viewTenantReducer);
  const saveTenantState = useSelector((state: any) => state.saveTenantReducer);
  const allPlansState = useSelector((state: any) => state.getAllPlansReducer);
  const accessControlState = useSelector((state: any) => state.getAccessControlReducer);
  const saveAccessControlState = useSelector((state: any) => state.saveAccessControlReducer);
  const routeListState = useSelector((state: any) => state.getRoutesReducer);
  const uploadState = useSelector((state: any) => state.updateProfileImageReducer);
  const availableRoutes = routeListState?.data || routeListState?.resultObject || [];

  const isLoading = Boolean(tenantViewState?.isLoading);
  const isUpdating = Boolean(saveTenantState?.isLoading);
  const isSavingAccess = Boolean(saveAccessControlState?.isLoading);
  const tenant = tenantViewState?.tenantData ?? null;
  const allPlans: any[] = allPlansState?.data ?? [];
  const isParentTenant = !tenant?.parentTenantId;
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const canEdit = role === 'SUPER_ADMIN' || role === 'ADMIN';

 

  const cycleType = tenant?.subscription?.plan?.billingCycle === 'monthly'
    ? 'month' : tenant?.subscription?.plan?.billingCycle === 'yearly'
      ? 'year' : tenant?.subscription?.plan?.billingCycle;

  const createdDateObj: Date | null = (() => {
    const v: any = tenant?.createdAt;
    if (!v) return null;
    const num = Number(v);
    if (!Number.isNaN(num)) {
      if (String(v).length === 10) return new Date(num * 1000);
      return new Date(num);
    }
    return new Date(String(v));
  })();

  useEffect(() => { dispatch(subscriptionActions.getRoutesRequest()); }, [dispatch]);
  useEffect(() => { if (tenantIdFromState) dispatch(tenantActions.viewTenant({ id: tenantIdFromState })); }, [dispatch, tenantIdFromState]);
  useEffect(() => {
    if (tenant && isParentTenant && isSuperAdmin)
      dispatch(tenantActions.getAccessControl(String(tenantIdFromState)));
  }, [tenant, isParentTenant, isSuperAdmin]);

  useEffect(() => {
    const existing = accessControlState?.data;
    if (existing?.accessPoints?.length) {
      setAccessPoints(existing.accessPoints.map((ap: any) => {
        const routeInfo = availableRoutes.find((r: any) => r.value === ap.route);
        return { ...ap, routeName: routeInfo?.label || ap.route };
      }));
    } else if (tenant?.subscription?.plan?.route?.length) {
      setAccessPoints(tenant.subscription.plan.route.map((route: string) => {
        const routeInfo = availableRoutes.find((r: any) => r.value === route);
        return { route, routeName: routeInfo?.label || route, ...DEFAULT_ACCESS_POINT, isFromSubscription: true };
      }));
    }
  }, [accessControlState?.data, tenant]);

  useEffect(() => {
    if (saveAccessControlState?.isSuccess && accessEditMode) {
      setAccessEditMode(false);
      dispatch(tenantActions.getAccessControl(String(tenantIdFromState)));
    }
  }, [saveAccessControlState?.isSuccess]);

  useEffect(() => {
    if (tenant) {
      form.setFieldsValue({
        name: tenant.name || '', email: tenant.email || '', phoneNumber: tenant.phoneNumber || '',
        country: tenant.country || '', address: tenant.address || '',
        validityStartDate: tenant.validityStartDate ? dayjs(tenant.validityStartDate) : null,
        validityEndDate: tenant.validityEndDate ? dayjs(tenant.validityEndDate) : null,
        customSubscriptionPlanId: tenant.subscription?.plan?.planId ?? null,
      });
      const imgUrl = tenant?.profileImageUrl || tenant?.profileImage?.url || '';
      setProfileImageUrl(imgUrl);
      setImageDeleted(false);
      setUploadedImageId(null);
      // Restore coordinates if exist
      if (tenant?.coordinates) {
        setCoordinates({ latitude: String(tenant.coordinates.latitude || ''), longitude: String(tenant.coordinates.longitude || '') });
      }
    }
  }, [tenant, form]);

  useEffect(() => {
    if (saveTenantState?.isSuccess && isEditMode) {
      setIsEditMode(false);
      if (tenantIdFromState) dispatch(tenantActions.viewTenant({ id: tenantIdFromState }));
    }
  }, [saveTenantState?.isSuccess, saveTenantState?.isError]);

  useEffect(() => {
    if (uploadState?.isSuccess) {
      const uploadedFile = uploadState?.profileImage;
      if (uploadedFile && uploadedFile._id) {
        setUploadedImageId(uploadedFile._id);
        setProfileImageUrl(uploadedFile.url || '');
      }
      setIsUploadingImage(false);
      setIsEditMode(false);
    }
  }, [uploadState]);

  const handleEditSave = async () => {
    try {
      const values = await form.validateFields();
      const payload: any = { _id: tenantIdFromState, ...values };
      if (values.validityStartDate) payload.validityStartDate = values.validityStartDate.toISOString();
      if (values.validityEndDate) payload.validityEndDate = values.validityEndDate.toISOString();
      payload.coordinates = { latitude: coordinates.latitude, longitude: coordinates.longitude };
      dispatch(tenantActions.saveTenant(payload));
    } catch (_) { }
  };

  const handleCancelEdit = () => {
    form.setFieldsValue({
      name: tenant?.name || '', email: tenant?.email || '', phoneNumber: tenant?.phoneNumber || '',
      country: tenant?.country || '', address: tenant?.address || '',
      validityStartDate: tenant?.validityStartDate ? dayjs(tenant.validityStartDate) : null,
      validityEndDate: tenant?.validityEndDate ? dayjs(tenant.validityEndDate) : null,
      customSubscriptionPlanId: tenant?.subscription?.plan?.planId ?? null,
    });
    const imgUrl = tenant?.profileImageUrl || tenant?.profileImage?.url || '';
    setProfileImageUrl(imgUrl);
    setImageDeleted(false);
    setUploadedImageId(null);
    if (tenant?.coordinates) setCoordinates({ latitude: String(tenant.coordinates.latitude || ''), longitude: String(tenant.coordinates.longitude || '') });
    else setCoordinates({ latitude: '', longitude: '' });
    setIsEditMode(false);
  };

  // Profile image handlers
  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) { message.error('You can only upload image files!'); return Upload.LIST_IGNORE; }
    const isLt3M = file.size / 1024 / 1024 < 3;
    if (!isLt3M) { message.error('Image must be smaller than 3MB!'); return Upload.LIST_IGNORE; }
    setProfileImageUrl('');
    setImageDeleted(false);
    setIsUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'tenantProfile');
    formData.append('ReferenceId', String(tenantIdFromState || ''));
    dispatch(userActions.uploadProfileImageRequest(formData));
    return false;
  };

  const handleDeleteImage = () => {
    if (uploadedImageId) {
      dispatch(commonActions.deleteFiles({ ids: [uploadedImageId] }));
      setUploadedImageId(null);
    } else {
      const currentImage = tenant?.profileImage;
      const fileId = currentImage?._id || (typeof currentImage === 'string' ? currentImage : null);
      if (fileId) dispatch(commonActions.deleteFiles({ ids: [fileId] }));
    }
    setProfileImageUrl('');
    setImageDeleted(true);
    setUploadedImageId(null);
  };

  const getProfileImage = () => profileImageUrl || undefined;

  // Location handler
  const handleGetLocation = () => {
    if (!navigator.geolocation) { message.error('Geolocation is not supported by your browser.'); return; }
    setIsFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setCoordinates({ latitude: String(latitude), longitude: String(longitude) });
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`);
          const data = await res.json();
          const detectedCountry = data?.address?.country;
          const updates: any = {};

          if (data?.display_name) {
            updates.address = data.display_name;
          }

          if (detectedCountry) {
            updates.country = detectedCountry;
          }

          if (Object.keys(updates).length > 0) {
            form.setFieldsValue(updates);
            message.success('Location details fetched successfully!');
          }
        } catch {
          message.warning('Location coordinates saved but address lookup failed.');
        }
        setIsFetchingLocation(false);
      },
      (err) => {
        message.error('Unable to retrieve your location. Please allow location access.');
        setIsFetchingLocation(false);
      }
    );
  };

  const handleAccessToggle = (routeIndex: number, field: string, value: boolean) => {
    setAccessPoints(prev => prev.map((ap, idx) => {
      if (idx !== routeIndex) return ap;
      const updated = { ...ap, [field]: value };
      if (field === 'isAllowed' && !value) return { ...updated, isAdd: false, isEdit: false, isView: false, isCalendarView: false, isLocationTracking: false, isLocationSharing: false };
      return updated;
    }));
  };

  const handleAddRoute = () => {
    if (!selectedRoutes.length) return;
    const routesToAdd = selectedRoutes.map(routeValue => {
      const routeInfo = availableRoutes.find((route: any) => route.value === routeValue);
      if (!routeInfo) return null;
      return { route: routeInfo.value, routeName: routeInfo.label, ...DEFAULT_ACCESS_POINT, isFromSubscription: false };
    }).filter(Boolean);
    setAccessPoints(prev => [...prev, ...routesToAdd]);
    setSelectedRoutes([]);
  };

  const handleRemoveRoute = (routeIndex: number) => setAccessPoints(prev => prev.filter((_, idx) => idx !== routeIndex));
  const handleSaveAccessControl = () => dispatch(tenantActions.saveAccessControl({ tenantId: tenantIdFromState, accessPoints }));

  const handleCancelAccess = () => {
    setAccessEditMode(false);
    const existing = accessControlState?.data;
    if (existing?.accessPoints?.length) {
      setAccessPoints(existing.accessPoints.map((ap: any) => {
        const routeInfo = availableRoutes.find((r: any) => r.value === ap.route);
        return { ...ap, routeName: routeInfo?.label || ap.route };
      }));
    }
  };

  const availableRouteOptions = useMemo(() => {
    const existingRoutes = accessPoints.map((item: any) => item.route);
    return availableRoutes.filter((route: any) => !existingRoutes.includes(route.value));
  }, [availableRoutes, accessPoints]);

  const getStatusColor = (isActive: boolean) => isActive ? 'success' : 'default';

  const getValidityInfo = useMemo(() => {
    const subscription = tenant?.subscription;
    if (!subscription || !subscription.endDate) {
      return { status: 'no_validity', color: 'default', icon: <InfoCircleOutlined />, title: 'No Active Subscription', description: 'No active subscription.', progress: null, daysRemaining: null, isExpired: false, isExpiringSoon: false };
    }
    const endDate = dayjs(subscription.endDate);
    const now = dayjs();
    const isExpired = endDate.isBefore(now) || subscription.status === 'expired' || subscription.status === 'cancelled';
    const daysRemaining = endDate.diff(now, 'day');
    const isExpiringSoon = daysRemaining > 0 && daysRemaining <= 7;
    let progress = null;
    if (subscription.startDate && !isExpired) {
      const totalDays = endDate.diff(dayjs(subscription.startDate), 'day');
      const elapsedDays = now.diff(dayjs(subscription.startDate), 'day');
      progress = { percent: totalDays > 0 ? Math.min(Math.round((elapsedDays / totalDays) * 100), 100) : 0 };
    }
    return {
      status: isExpired ? 'expired' : isExpiringSoon ? 'expiring' : 'active',
      color: isExpired ? 'error' : isExpiringSoon ? 'warning' : 'success',
      icon: isExpired ? <CloseCircleOutlined /> : isExpiringSoon ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />,
      title: isExpired ? (subscription.status === 'cancelled' ? 'Cancelled' : 'Expired') : isExpiringSoon ? 'Expiring Soon' : 'Active Subscription',
      description: isExpired ? `Expired on ${endDate.format('DD MMM YYYY')}` : `Valid until ${endDate.format('DD MMM YYYY')}`,
      progress, daysRemaining: !isExpired ? daysRemaining : null, isExpired, isExpiringSoon
    };
  }, [tenant]);

  const goToPurchaseHistory = () => navigate('/tenants/purchaseHistory', {
    state: { tenantId: tenantIdFromState, tenantName: tenant?.name, tenantEmail: tenant?.email, tenantAddress: tenant?.address },
  });

  if (isLoading) return <div className='view-loading'><SpinnerLoader /></div>;
  if (!tenant) return (
    <div className="tenant-view-empty">
      <Empty description="Tenant not found" />
      <Button type="primary" onClick={() => navigate('/tenants')}>Back</Button>
    </div>
  );

  const createdLabel = createdDateObj
    ? createdDateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  const hasImage = !!profileImageUrl && !imageDeleted;

  return (
    <div className="batch-view-page">
      {/* Header */}
      <div className="tenant-view" style={{ justifyContent: role === 'SUPER_ADMIN' ? "space-between" : "none" }}>
        <Button type="text" icon={<ArrowLeftOutlined />} style={{ display: role === 'SUPER_ADMIN' ? "flex" : "none" }} onClick={() => navigate(role === 'ADMIN' ? '/dashboard' : '/tenants')} className="batch-back-btn">Tenants</Button>
        <div className="batch-header-right">
          {createdLabel && <span className="batch-created">Created {createdLabel}</span>}
          <Button icon={<FileTextOutlined />} onClick={goToPurchaseHistory} style={{ marginRight: 4 }}>Purchase History</Button>
          {canEdit && (!isEditMode ? (
            <Button className="batch-edit-btn" icon={<EditOutlined />} onClick={() => { dispatch(subscriptionActions.getAllPlansRequest()); setIsEditMode(true); }}>Edit Tenant</Button>
          ) : (
            <Space>
              <Button icon={<CloseOutlined />} onClick={handleCancelEdit}>Cancel</Button>
              <Button type="primary" icon={<SaveOutlined />} loading={isUpdating || isUploadingImage} onClick={handleEditSave}>Save Changes</Button>
            </Space>
          ))}
        </div>
      </div>

      {/* Hero */}
      <div className="batch-hero">
        <Spin spinning={isUploadingImage} size="small" wrapperClassName="uv-avatar-upload-spinner">
          <Avatar size={60} src={getProfileImage()} icon={<BankOutlined />} style={{ flexShrink: 0 }} />
        </Spin>
        <div className="batch-hero-info">
          <div className="batch-hero-titlerow">
            <h2 className="batch-hero-name">{tenant?.name || 'N/A'}</h2>
            <span className={`batch-status-pill batch-status-pill--${tenant?.isActive ? 'active' : 'inactive'}`}>
              <span className="batch-status-dot" />
              {tenant?.isActive ? 'ACTIVE' : 'INACTIVE'}
            </span>
          </div>
          <div className="batch-hero-meta">
            {tenant?.email && <span className="batch-hero-meta-item"><MailOutlined /> {tenant.email}</span>}
            {tenant?.phoneNumber && <span className="batch-hero-meta-item"><PhoneOutlined /> {tenant.phoneNumber}</span>}
            {tenant?.country && <span className="batch-hero-meta-item"><GlobalOutlined /> {tenant.country}</span>}
          </div>
          {isEditMode && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <Upload showUploadList={false} beforeUpload={beforeUpload} accept="image/*">
                <Button size="small" icon={<CameraOutlined />}>{hasImage ? 'Change Logo' : 'Upload Logo'}</Button>
              </Upload>
              {hasImage && <Button size="small" icon={<DeleteOutlined />} danger onClick={handleDeleteImage}>Remove</Button>}
            </div>
          )}
        </div>
      </div>

      {/* Tenant fields */}
      {isEditMode ? (
        <Card className="entity-details-card" bordered={false} style={{ marginBottom: 16 }}>
          <Form form={form} layout="vertical">
            <Row gutter={[16, 12]}>
              <Col xs={24} md={12}>
                <Form.Item name="name" label="Tenant Name" rules={[{ required: true, message: 'Please enter tenant name' }]}>
                  <Input prefix={<BankOutlined style={{ color: '#8c8c8c' }} />} placeholder="Enter tenant name" style={{ height: 40 }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Please enter email' }, { type: 'email', message: 'Please enter a valid email' }]}>
                  <Input prefix={<MailOutlined style={{ color: '#8c8c8c' }} />} placeholder="Enter email" style={{ height: 40 }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="phoneNumber" label="Phone Number" rules={[{ required: true, message: 'Please enter phone number' }, { pattern: /^\d{10}$/, message: 'Please enter a valid 10-digit phone number' }]}>
                  <Input prefix={<PhoneOutlined style={{ color: '#8c8c8c' }} />} placeholder="Enter 10-digit phone number" maxLength={10} style={{ height: 40 }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="country" label="Country">
                  <Input prefix={<GlobalOutlined style={{ color: '#8c8c8c' }} />} placeholder="Enter country (e.g. India)" style={{ height: 40 }} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="address"
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <span>Address</span>
                    </div>
                  }>
                  <Input
                    prefix={<EnvironmentOutlined style={{ color: '#8c8c8c' }} />}
                    placeholder="Enter complete address or use location detector"
                    style={{ height: 40 }}
                    suffix={
                      <Tooltip title="Auto-detect current location coordinates, address, and country">
                        <Button
                          type="link"
                          size="small"
                          icon={<AimOutlined />}
                          loading={isFetchingLocation}
                          onClick={handleGetLocation}
                          style={{ padding: '0 4px', height: 'auto', fontSize: 13 }}
                        >
                          {isFetchingLocation ? 'Detecting...' : 'Use my location'}
                        </Button>
                      </Tooltip>
                    }
                  />
                </Form.Item>
              </Col>
              {role === 'SUPER_ADMIN' && !tenant?.parentTenantId && (
                <Col xs={24}>
                  <Divider>Custom Subscription</Divider>
                  <Form.Item name="customSubscriptionPlanId" label="Subscription Plan">
                    <Select allowClear placeholder="Select a subscription plan" loading={allPlansState?.isLoading}
                      style={{ height: 40 }}
                      options={allPlans
                        .filter((p: any) => (p.isActive !== false && p.name !== 'Free Trial') || p._id === tenant?.subscription?.plan?.planId)
                        .map((p: any) => ({
                          label: `${p.name} — ₹${p.price}/${p.billingCycle}`,
                          value: p._id,
                          disabled: p._id === tenant?.subscription?.plan?.planId && (p.isActive === false || p.name === 'Free Trial'),
                        }))} />
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate={(prev, curr) => prev.customSubscriptionPlanId !== curr.customSubscriptionPlanId}>
                    {({ getFieldValue }) => {
                      const hasPlan = !!getFieldValue('customSubscriptionPlanId');
                      return (
                        <Row gutter={[16, 8]}>
                          <Col xs={24} sm={12}>
                            <Form.Item name="validityStartDate" label="Validity Start Date" rules={[{ required: hasPlan }]}>
                              <DatePicker style={{ width: '100%', height: 40 }} format="DD/MM/YYYY" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item name="validityEndDate" label="Validity End Date" dependencies={['validityStartDate']}
                              rules={[{ required: hasPlan }, ({ getFieldValue }) => ({ validator(_, value) { const s = getFieldValue('validityStartDate'); if (!value || !s || value.isAfter(s)) return Promise.resolve(); return Promise.reject(new Error('End date must be after start date')); } })]}>
                              <DatePicker style={{ width: '100%', height: 40 }} format="DD/MM/YYYY" disabledDate={(c) => { const s = form.getFieldValue('validityStartDate'); return s ? c && c.isBefore(s, 'day') : false; }} />
                            </Form.Item>
                          </Col>
                        </Row>
                      );
                    }}
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Form>
        </Card>
      ) : (
        <div className="tenant-details-card">
          <div className="uv-card-header">
            <BankOutlined className="uv-card-header-anticon" />
            <span className="uv-card-header-title">Organization Details</span>
          </div>
          <div className="uv-personal-grid">
            <div className="uv-pd-item"><span className="uv-pd-label">Email</span><span className="uv-pd-value">{tenant?.email || '-'}</span></div>
            <div className="uv-pd-item"><span className="uv-pd-label">Phone</span><span className="uv-pd-value">{tenant?.phoneNumber || '-'}</span></div>
            <div className="uv-pd-item"><span className="uv-pd-label">Country</span><span className="uv-pd-value">{tenant?.country || '-'}</span></div>
            <div className="uv-pd-item">
              <span className="uv-pd-label">Address</span>
              <span className="uv-pd-value" style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                {tenant?.address ? <><EnvironmentOutlined style={{ color: '#1890ff', marginTop: 2, flexShrink: 0 }} />{tenant.address}</> : '-'}
              </span>
            </div>
            {tenant?.coordinates?.latitude && (
              <div className="uv-pd-item">
                <span className="uv-pd-label">Coordinates</span>
                <span className="uv-pd-value" style={{ fontSize: 12, color: '#8c8c8c' }}>
                  <AimOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                  {Number(tenant.coordinates.latitude).toFixed(5)}, {Number(tenant.coordinates.longitude).toFixed(5)}
                </span>
              </div>
            )}
            <div className="uv-pd-item"><span className="uv-pd-label">Created</span><span className="uv-pd-value">{createdDateObj ? createdDateObj.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</span></div>
          </div>
        </div>
      )}

      <div className="tenant-cards-stack">
        <Card className="tenant-sub-card" bordered={false}
          title={<span style={{ fontWeight: 700, fontSize: 16 }}>Subscription Plan</span>}
          extra={tenant?.subscription?.status === 'active' ? (<Tag icon={<CheckCircleOutlined />} color="success" style={{ fontWeight: 600, fontSize: 12, padding: '2px 10px', borderRadius: 20, border: "1px solid" }}>ACTIVE SUBSCRIPTION</Tag>) : tenant?.subscription?.status === 'expired' ? (<Tag icon={<CloseCircleOutlined />} color="error" style={{ fontWeight: 600, fontSize: 12, padding: '2px 10px', borderRadius: 20 }}>EXPIRED</Tag>) : null}
        >
          {tenant?.subscription?.plan ? (
            <>
              <div className="tsub-plan-row">
                <Space size={14} align="center">
                  <div className="tsub-plan-icon"><TbTrophy size={22} style={{ color: '#1890ff' }} /></div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', letterSpacing: 0.5, textTransform: 'uppercase' }}>Plan Name</Text>
                    <Space size={6} align="center">
                      <Text strong style={{ fontSize: 20 }}>{tenant.subscription.plan.name}</Text>
                      {tenant.subscription.subscriptionType === 'custom' && (<Tag color="blue" style={{ borderRadius: 10, fontSize: 11, fontWeight: 600 }}>CUSTOM</Tag>)}
                    </Space>
                  </div>
                </Space>
                <div style={{ textAlign: 'right' }}>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', letterSpacing: 0.5, textTransform: 'uppercase' }}>{cycleType === 'month' ? 'Monthly Price' : 'Yearly Price'}</Text>
                  <Text strong style={{ fontSize: 20 }}>{tenant.subscription.plan.price}<Text type="secondary" style={{ fontSize: 14, fontWeight: 400 }}>{cycleType === 'month' ? '/mo' : '/yr'}</Text></Text>
                </div>
              </div>
              <Divider style={{ margin: '16px 0' }} />
              <Row gutter={[0, 0]} className="tsub-info-grid">
                <Col xs={12} md={6} className="tsub-info-cell"><Text type="secondary" style={{ fontSize: 11, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Billing Cycle</Text><Text strong style={{ fontSize: 13, textTransform: 'capitalize' }}>{tenant.subscription.plan.billingCycle}</Text></Col>
                <Col xs={12} md={6} className="tsub-info-cell"><Text type="secondary" style={{ fontSize: 11, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Auto Renewal</Text><Text strong style={{ fontSize: 13, color: tenant.subscription.autoRenewal ? '#52c41a' : '#ff4d4f' }}>{tenant.subscription.autoRenewal ? 'Enabled' : 'Disabled'}</Text></Col>
              </Row>
              {tenant.subscription.startDate && tenant.subscription.endDate && (
                <>
                  <Divider style={{ margin: '16px 0' }} />
                  <div className="tsub-validity-box">
                    <div className="tsub-validity-header">
                      <Text strong style={{ fontSize: 14, marginRight: '10px' }}>Validity Progress</Text>
                      <Text strong style={{ fontSize: 14, color: '#1890ff' }}>{getValidityInfo.daysRemaining !== null ? `${getValidityInfo.daysRemaining} Days` : 'Expired'}</Text>
                    </div>
                    <div className="tsub-validity-sub">
                      <Text type="secondary" style={{ fontSize: 12, marginRight: '10px' }}>{dayjs(tenant.subscription.startDate).format('DD MMM YYYY')} to {dayjs(tenant.subscription.endDate).format('DD MMM YYYY')}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>Remaining ({getValidityInfo.progress?.percent ?? 0}% elapsed)</Text>
                    </div>
                    <Progress percent={getValidityInfo.progress?.percent ?? 0} showInfo={false} strokeColor={getValidityInfo.isExpired ? '#ff4d4f' : getValidityInfo.isExpiringSoon ? '#faad14' : '#1890ff'} trailColor="#f0f0f0" style={{ marginTop: 8 }} />
                  </div>
                </>
              )}
            </>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Space direction="vertical" size={4}><Text>No subscription active.</Text><Text type="secondary">Tenant needs to subscribe to a plan.</Text></Space>} />
          )}
        </Card>

        {isParentTenant && isSuperAdmin && (
          <Card
            className="tenant-access-card"
            bordered={false}
            style={{ marginTop: 16 }}
            title={
              <Space>
                <SettingOutlined style={{ color: '#1890ff' }} />
                <span style={{ fontWeight: 700, fontSize: 16 }}>Access Management</span>
                <Tag color="blue" style={{ borderRadius: 10, fontSize: 11 }}>Feature Control</Tag>
              </Space>
            }
            extra={
              !accessEditMode ? (
                <Button type="primary" icon={<EditOutlined />} size="small" onClick={() => setAccessEditMode(true)}>Configure</Button>
              ) : (
                <Space>
                  <Button size="small" icon={<CloseOutlined />} onClick={() => { handleCancelAccess(); setSelectedRoutes([]); }}>Cancel</Button>
                  <Button type="primary" size="small" icon={<SaveOutlined />} loading={isSavingAccess} onClick={handleSaveAccessControl}>Save</Button>
                </Space>
              )
            }
          >
            {accessControlState?.isLoading ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}><Spin /></div>
            ) : (
              <div className="access-points-container">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Configure which pages and features are accessible for this tenant. Disabling <strong>Page Access</strong> hides the page entirely.
                </Text>

                {accessEditMode && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, marginBottom: 4 }}>
                    <Select mode="multiple" allowClear maxTagCount="responsive" style={{ flex: 1 }} placeholder="Select Routes" value={selectedRoutes}
                      options={availableRouteOptions.map(
                        (route: any) => ({
                          label: route.label,
                          value: route.value,
                        })
                      )}
                      onChange={(values) =>
                        setSelectedRoutes(values)
                      }
                    />
                    <Button icon={<PlusOutlined />} type="dashed" disabled={!selectedRoutes.length} onClick={handleAddRoute}>
                      Add Route
                    </Button>
                  </div>
                )}

                {accessPoints.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 16 }} description={<Text type="secondary">No access points configured. Subscribe to a plan first or add a route above.</Text>} />
                ) : (
                  <Collapse ghost className="access-collapse" style={{ marginTop: 12 }} defaultActiveKey={[]}>
                    {accessPoints.map((ap: any, idx: number) => {
                      const fieldMap = getFieldsForRoute(ap.route, ap.isFromSubscription);
                      const allEnabled = Object.keys(fieldMap).every(k => ap[k] === true);
                      const isBlocked = !ap.isAllowed;
                      const isSubscriptionRoute = ap.isFromSubscription === true;

                      return (
                        <Panel
                          key={idx}
                          className={`access-panel ${isBlocked ? 'access-panel--blocked' : allEnabled ? 'access-panel--full' : 'access-panel--partial'}`}
                          header={
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 8 }}>
                              <Space size={8} align="center">
                                {isBlocked ? <LockOutlined style={{ color: '#ff4d4f' }} /> : <UnlockOutlined style={{ color: '#52c41a' }} />}
                                {/* <Text strong style={{ fontSize: 13, fontFamily: 'monospace' }}>{ap.route}</Text> */}
                                <div style={{ display: 'flex', flexDirection: 'column', }}>
                                  <Text strong> {ap.routeName || ap.route}</Text>
                                  <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace', }} >{ap.route}</Text>
                                </div>
                                <Tag color={isBlocked ? 'error' : allEnabled ? 'success' : 'warning'} style={{ borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                                  {isBlocked ? 'BLOCKED' : allEnabled ? 'FULL ACCESS' : 'PARTIAL'}
                                </Tag>
                                {isSubscriptionRoute && (
                                  <Tag color="purple" style={{ borderRadius: 10, fontSize: 10 }}>Plan</Tag>
                                )}
                              </Space>
                              <Space size={4} style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                {!isBlocked && Object.keys(fieldMap).filter(k => k !== 'isAllowed').map(k =>
                                  ap[k] ? <Tag key={k} color="cyan" style={{ fontSize: 10, borderRadius: 8, margin: '1px 0' }}>{fieldMap[k as keyof typeof fieldMap]}</Tag> : null
                                )}
                                {/* Only allow delete for non-subscription routes */}
                                {accessEditMode && !isSubscriptionRoute && (
                                  <Tooltip title="Remove route">
                                    <Button
                                      size="small"
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={(e) => { e.stopPropagation(); handleRemoveRoute(idx); }}
                                      style={{ marginLeft: 4 }}
                                    />
                                  </Tooltip>
                                )}
                                {accessEditMode && isSubscriptionRoute && (
                                  <Tooltip title="This route is from your subscription plan and cannot be removed">
                                    <LockOutlined style={{ color: '#722ed1', marginLeft: 4, fontSize: 13 }} />
                                  </Tooltip>
                                )}
                              </Space>
                            </div>
                          }
                        >
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '10px 24px',
                            padding: '8px 4px',
                          }}>
                            {Object.keys(fieldMap).map(field => (
                              <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 140 }}>
                                <Switch
                                  size="small"
                                  checked={!!ap[field]}
                                  disabled={!accessEditMode || (field !== 'isAllowed' && !ap.isAllowed)}
                                  onChange={(val) => handleAccessToggle(idx, field, val)}
                                />
                                <Text style={{ fontSize: 13 }}>{fieldMap[field as keyof typeof fieldMap]}</Text>
                                {field === 'isAllowed' && !ap.isAllowed && (
                                  <Tag color="error" style={{ borderRadius: 8, fontSize: 10 }}>Hidden</Tag>
                                )}
                              </div>
                            ))}
                          </div>
                        </Panel>
                      );
                    })}
                  </Collapse>
                )}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default TenantView;


